'use client';

import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

const TYPING_MS = 45;
const DELETING_MS = 28;
const PAUSE_MS = 1_800;

function scrollTrackToEnd(track: HTMLElement | null) {
  if (!track) return;
  track.scrollLeft = track.scrollWidth;
}

export function useTypewriterCycle(
  phrases: string[],
  textRef: RefObject<HTMLElement | null>,
  enabled: boolean,
  trackRef?: RefObject<HTMLElement | null>
): RefObject<string> {
  const phraseIndex = useRef(0);
  const currentPhraseRef = useRef(phrases[0] ?? '');

  useEffect(() => {
    const node = textRef.current;
    // keep currentPhraseRef when pausing (e.g. input focus) so empty submit
    // still uses the phrase that was on screen
    if (!enabled || !phrases.length || !node) {
      if (node) node.textContent = '';
      return;
    }

    // only reset when (re)starting a cycle
    phraseIndex.current = 0;
    currentPhraseRef.current = phrases[0] ?? '';

    let charIndex = 0;
    let isDeleting = false;
    let timer: ReturnType<typeof setTimeout>;

    const currentPhrase = () => phrases[phraseIndex.current % phrases.length];

    const render = () => {
      currentPhraseRef.current = currentPhrase();
      node.textContent = currentPhrase().slice(0, charIndex);
      scrollTrackToEnd(trackRef?.current ?? null);
    };

    const tick = () => {
      if (!isDeleting) {
        charIndex += 1;
        render();

        if (charIndex >= currentPhrase().length) {
          timer = setTimeout(() => {
            isDeleting = true;
            tick();
          }, PAUSE_MS);
          return;
        }

        timer = setTimeout(tick, TYPING_MS);
        return;
      }

      charIndex -= 1;
      render();

      if (charIndex <= 0) {
        phraseIndex.current += 1;
        isDeleting = false;
        timer = setTimeout(tick, TYPING_MS);
        return;
      }

      timer = setTimeout(tick, DELETING_MS);
    };

    node.textContent = '';
    timer = setTimeout(tick, TYPING_MS);

    return () => {
      clearTimeout(timer);
      node.textContent = '';
    };
  }, [phrases, enabled, textRef, trackRef]);

  return currentPhraseRef;
}
